import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import SEO from "@/components/SEO";
import { difficultyLabel } from "@/i18n";
import { ArrowRight, Footprints } from "lucide-react";

const Tappe = () => {
  const { t, lang } = useLang();
  const [tappe, setTappe] = useState([]);

  useEffect(() => { api.get("/tappe").then((r) => setTappe(r.data)); }, []);

  const bySec = (s) => tappe.filter((t) => t.section === s);
  const sections = [
    { key: "catania-siracusa", label: t("section_catania_siracusa") },
    { key: "siracusa-ragusa", label: t("section_siracusa_ragusa") },
    { key: "extra", label: t("section_extra") },
  ];

  return (
    <div data-testid="tappe-page">
      <SEO
        titleIt="Tappe del Cammino — 10 tappe + percorsi pedonali (285 km)"
        titleEn="Trail stages — 10 stages + walking routes (285 km)"
        descriptionIt="Tutte le tappe del Cammino Ibleo UNESCO: dettagli km, dislivello, tempi a piedi e in bici, siti UNESCO attraversati, mappa GPS per ogni tratto."
        descriptionEn="All the stages of the Cammino Ibleo UNESCO: distance, elevation, walking and cycling times, UNESCO sites crossed, GPS map for each section."
      />
      <section className="pt-40 pb-16 bg-pietra-light border-b border-carrubo/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="eyebrow mb-6">285 km · 10 tappe + percorsi pedonali</div>
          <h1 className="font-serif text-5xl md:text-7xl text-grafite mb-4">{t("nav_tappe")}</h1>
          <p className="text-lg text-grafite-soft max-w-3xl">
            {lang === "it"
              ? "Il cammino si sviluppa in due grandi sezioni: Catania → Siracusa (nord) e Siracusa → Ragusa (sud). Ogni tappa termina in un centro storico o UNESCO."
              : "The trail unfolds in two large sections: Catania → Syracuse (north) and Syracuse → Ragusa (south). Each stage ends at a historic or UNESCO centre."}
          </p>
        </div>
      </section>

      <section className="py-20 bg-pietra-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 space-y-16">
          {sections.map((sec) => {
            const items = bySec(sec.key);
            if (!items.length) return null;
            return (
              <div key={sec.key}>
                <div className="flex items-center gap-4 mb-8">
                  <h2 className="font-serif text-3xl md:text-4xl text-grafite">{sec.label}</h2>
                  <div className="flex-1 railway-line opacity-40" />
                </div>
                <div className="space-y-4">
                  {items.map((tp) => (
                    <Link key={tp.slug} to={`/tappe/${tp.slug}`} data-testid={`tappa-item-${tp.slug}`}
                      className="group grid md:grid-cols-[240px_1fr_auto] gap-6 items-center p-5 md:p-6 bg-pietra rounded-sm border border-carrubo/10 hover:border-carrubo/50 transition-all">
                      <div className="relative overflow-hidden rounded-sm aspect-[16/10]">
                        <img loading="lazy" decoding="async" src={tp.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute top-3 left-3 bg-pietra-light/95 backdrop-blur px-2 py-1 rounded-sm font-serif text-xl text-carrubo">
                          {String(tp.order).padStart(2, "0")}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <StatusBadge status={tp.status} />
                          <span className="text-xs uppercase tracking-widest text-grafite-soft">{difficultyLabel(tp.difficulty, lang)}</span>
                          {tp.type === "pedestrian" && (
                            <span className="text-xs uppercase tracking-widest text-carrubo flex items-center gap-1">
                              <Footprints size={12} /> {t("tappa_type_walk")}
                            </span>
                          )}
                        </div>
                        <h3 className="font-serif text-2xl md:text-3xl text-grafite mb-1">{tp[lang].title}</h3>
                        <p className="text-grafite-soft italic mb-3">{tp[lang].subtitle}</p>
                        <div className="flex flex-wrap gap-5 text-sm text-grafite">
                          <span><strong className="font-serif text-lg text-carrubo">{tp.km}</strong> km</span>
                          <span>↑ <strong>{tp.elevation_gain_m}</strong> m</span>
                          <span>{t("tappa_walk")}: <strong>{tp.walk_hours}</strong></span>
                          {tp.bike_hours && <span>{t("tappa_bike")}: <strong>{tp.bike_hours}</strong></span>}
                          {tp.ebike_hours && <span className="text-miele-dark">{t("tappa_ebike")}: <strong>{tp.ebike_hours}</strong></span>}
                        </div>
                      </div>
                      <div className="hidden md:flex text-carrubo group-hover:text-miele">
                        <ArrowRight size={28} strokeWidth={1.3} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Tappe;
